/**
 * The input for one value of a member custom field, by its System field type (text, textarea, number, decimal,
 * boolean, date, datetime, select, email, phone, url). Values are kept as the text the API stores (dates
 * yyyy-MM-dd, booleans true/false); the API normalises and validates them again.
 * Multi-select fields use <CustomFieldValuesInput>, which renders one MultiSelect.
 */
import { IconPlus, IconX } from '@tabler/icons-react';
import {
  TextInput, Textarea, NumberInput, Select, SegmentedControl, DatePickerInput, DateTimePicker, MultiSelect, Stack, Group, Button, ActionIcon,
  Tooltip, Text,
} from '@/components/ui';
import { allowsMultiple, dateFromValue, dateToValue, dateTimeToValue } from './memberDetailsFormat';

export interface CustomFieldInputDef {
  label: string;
  fieldTypeCode?: string | null;
  options: string[];
  placeholder?: string | null;
  helpText?: string | null;
  allowMultiple: boolean;
}

interface SingleProps {
  def: CustomFieldInputDef;
  value: string;
  onChange: (value: string) => void;
  label?: string;
  error?: string | null;
}

export function CustomFieldValueInput({ def, value, onChange, label, error }: SingleProps) {
  const placeholder = def.placeholder ?? undefined;
  const common = { label, error: error ?? undefined };
  switch (def.fieldTypeCode) {
    case 'textarea':
      return <Textarea {...common} placeholder={placeholder} autosize minRows={2} maxRows={8} maxLength={4000} value={value} onChange={(e) => onChange(e.currentTarget.value)} />;
    case 'number':
      return <NumberInput {...common} placeholder={placeholder} allowDecimal={false} thousandSeparator=" " value={value === '' ? '' : Number(value)}
        onChange={(v) => onChange(v === '' || v === null || v === undefined ? '' : String(v))} />;
    case 'decimal':
      return <NumberInput {...common} placeholder={placeholder} decimalScale={6} thousandSeparator=" " value={value === '' ? '' : Number(value)}
        onChange={(v) => onChange(v === '' || v === null || v === undefined ? '' : String(v))} />;
    case 'boolean':
      return (
        <Stack gap={4}>
          {label && <Text size="sm" fw={500}>{label}</Text>}
          <SegmentedControl value={value || ''} onChange={onChange}
            data={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]} />
          {error && <Text size="xs" c="red">{error}</Text>}
        </Stack>
      );
    case 'date':
      return <DatePickerInput {...common} placeholder={placeholder ?? 'Pick a date'} clearable valueFormat="D MMMM YYYY"
        value={dateFromValue(value)} onChange={(d) => onChange(dateToValue((d as Date | null) ?? null))} />;
    case 'datetime':
      return <DateTimePicker {...common} placeholder={placeholder ?? 'Pick a date and time'} clearable valueFormat="D MMMM YYYY HH:mm"
        value={dateFromValue(value)} onChange={(d) => onChange(dateTimeToValue((d as Date | null) ?? null))} />;
    case 'select':
      return <Select {...common} placeholder={placeholder ?? 'Choose'} data={def.options} value={value || null} searchable={def.options.length > 8}
        onChange={(v) => onChange(v ?? '')} clearable />;
    case 'email':
      return <TextInput {...common} type="email" placeholder={placeholder ?? 'name@example.org'} value={value} onChange={(e) => onChange(e.currentTarget.value)} />;
    case 'phone':
      return <TextInput {...common} type="tel" placeholder={placeholder ?? 'For example 082 123 4567'} value={value} onChange={(e) => onChange(e.currentTarget.value)} />;
    case 'url':
      return <TextInput {...common} type="url" placeholder={placeholder ?? 'https://'} value={value} onChange={(e) => onChange(e.currentTarget.value)} />;
    default:
      return <TextInput {...common} placeholder={placeholder} maxLength={4000} value={value} onChange={(e) => onChange(e.currentTarget.value)} />;
  }
}

interface MultiProps {
  def: CustomFieldInputDef;
  values: string[];
  onChange: (values: string[]) => void;
  error?: string | null;
}

/** All the values of a field: one input, several (with "Add another") or a MultiSelect. */
export function CustomFieldValuesInput({ def, values, onChange, error }: MultiProps) {
  if (def.fieldTypeCode === 'multiselect') {
    return <MultiSelect label={def.label} description={def.helpText ?? undefined} placeholder={def.placeholder ?? 'Choose'} data={def.options}
      value={values} onChange={onChange} searchable clearable error={error ?? undefined} />;
  }
  if (!allowsMultiple(def.fieldTypeCode, def.allowMultiple)) {
    return (
      <Stack gap={4}>
        <CustomFieldValueInput def={def} label={def.label} value={values[0] ?? ''} onChange={(v) => onChange([v])} error={error} />
        {def.helpText && <Text size="xs" c="dimmed">{def.helpText}</Text>}
      </Stack>
    );
  }
  const rows = values.length > 0 ? values : [''];
  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>{def.label}</Text>
      {def.helpText && <Text size="xs" c="dimmed">{def.helpText}</Text>}
      {rows.map((v, i) => (
        <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
          <div style={{ flex: 1 }}>
            <CustomFieldValueInput def={def} value={v} onChange={(next) => onChange(rows.map((x, j) => (j === i ? next : x)))} />
          </div>
          {rows.length > 1 && (
            <Tooltip label="Remove this value">
              <ActionIcon variant="subtle" color="red" mt={4} aria-label="Remove this value" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
                <IconX size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      ))}
      {error && <Text size="xs" c="red">{error}</Text>}
      <Group>
        <Button size="xs" variant="subtle" leftSection={<IconPlus size={14} />} onClick={() => onChange([...rows, ''])}>Add another</Button>
      </Group>
    </Stack>
  );
}
